import { useActiveConversation } from '../components/chat/ActiveConversationContext';
import { useGenericBi } from './use-generic-bi';
import { briefAgentIds, briefIsInMotion, briefIsShowable, maestroSummary, useMorningBrief } from './brief';

// Shared view-model for the Morning Brief hero card — desktop (`MaestroBrief`) and
// mobile (`MobileMaestroBrief`) render this differently but read the same data.

export interface MaestroBriefViewModel {
  agentIds: string[];
  agentCount: number;
  time: string;
  // The meeting's title (its topic) — shown as the conversation name. Empty while the meeting is still
  // convening and hasn't been named yet; the card shows a generic in-progress label then.
  title: string;
  // The meeting is still convening (created today, not yet wrapped up). The card shows an "in progress"
  // treatment — the title (or a generic label), the agents who have joined so far, and no CTA to a
  // finished summary.
  inProgress: boolean;
  headline?: string;
  open: () => void;
}

export function formatBriefTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

// The summary turn is markdown prose, but the glance headline is plain text (only
// **bold** is honored — see BriefHeadline). The wrap-up often opens with block
// markdown (a `---` rule + a `## heading`), which leaked into the headline as
// literal "--- ##". Strip block-level syntax (thematic breaks, ATX headings,
// blockquotes, list bullets) and inline links/`code`, keeping the prose (and the
// `**` pairs BriefHeadline renders).
function stripHeadlineMarkdown(text: string): string {
  return text
    .split('\n')
    .filter((line) => !/^\s*([-*_])\1{2,}\s*$/.test(line))
    .map((line) =>
      line
        .replace(/^\s*#{1,6}\s+/, '')
        .replace(/^\s*>\s?/, '')
        .replace(/^\s*(?:[-*+]|\d+\.)\s+/, ''),
    )
    .join('\n')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

function firstSentence(text: string): string {
  const trimmed = stripHeadlineMarkdown(text);
  const idx = trimmed.indexOf('.');
  return idx === -1 ? trimmed : trimmed.slice(0, idx + 1);
}

// `null` when there's nothing to show (no brief yet, or an empty one) — callers
// fall back to whatever BriefSlot renders for that state.
export function useMaestroBrief(): MaestroBriefViewModel | null {
  const { logPageClickBi } = useGenericBi();
  const { openBriefConversation } = useActiveConversation();
  const brief = useMorningBrief();

  // Not showable (no brief, failed, or a stale strand) => the slot renders one of its other states, never
  // a prior day's brief. A brief still IN MOTION is showable even with no turns yet.
  if (!briefIsShowable(brief) || !brief) return null;

  const inProgress = briefIsInMotion(brief);
  const agentIds = briefAgentIds(brief);
  const time = formatBriefTime(brief.generatedDate);
  const summary = maestroSummary(brief);
  // A convening brief has no wrap-up summary yet — no headline. A finished one uses the teaser/summary.
  const headline = inProgress ? undefined : brief.teaser || (summary ? firstSentence(summary.content) : undefined);

  const open = () => {
    logPageClickBi({ page_name: 'chat_home', page_version: 'v3_chat_home', button_name: 'brief_open_thread' });
    openBriefConversation(brief.conversationId);
  };

  // `name?.trim() ?? ''` — an in-motion brief has NO name yet (see `MorningBrief.name`), and the card
  // falls back to the generic time label for an empty title. A bare `.trim()` threw on every convening
  // brief from #7155 until this guard.
  return { agentIds, agentCount: agentIds.length, time, title: brief.name?.trim() ?? '', inProgress, headline, open };
}
